import { queryOptions, useMutation, useQueryClient, useSuspenseQuery, useQuery } from '@tanstack/react-query'
import { TWC_BASE_KEY, createQueryKey } from '@/api/query-keys'
import {
  ReceivablePeriodDataResponse,
  ReceivableTotals,
  ReceivableAllowedTypes,
  ReceivableBaseFieldsKeys,
  ReceivableSetUpResponse,
  ReceivableDataPost,
  ReceivableSetup
} from '../types'
import { PeriodType, PeriodTypeQuery, PeriodTypeView } from '@/types'

import { axiosInstance } from '@/api/axios'
import { TWC_TOTALS_KEY } from './totals'

export const RECEIVABLE_KEY = createQueryKey({
  baseKey: TWC_BASE_KEY,
  keys: [`receivable`]
})

const PERIOD_DATA_KEY = createQueryKey({
  baseKey: TWC_BASE_KEY,
  keys: [`receivable`, `period-data`]
})

const TOTALS_KEY = TWC_TOTALS_KEY.concat([`receivable`])

const getReceivableSetup = async (financialModelId: string) => {
  const response = await axiosInstance.get<ReceivableSetUpResponse>(`/financial-models/${financialModelId}/receivables/setup/`)
  return response.data
}

export const getReceivableSetupQueryOptions = (financialModelId: string) =>
  queryOptions({
    queryKey: RECEIVABLE_KEY.concat([`setup`, financialModelId]),
    queryFn: () => getReceivableSetup(financialModelId)
  })

export const useGetReceivableSetup = (financialModelId: string) => useSuspenseQuery(getReceivableSetupQueryOptions(financialModelId))

const updateReceivableSetup = async (financialModelId: string, params: ReceivableSetup) => {
  const response = await axiosInstance.post<ReceivableSetUpResponse>(`/financial-models/${financialModelId}/receivables/setup`, params)
  return response.data
}

export const useUpdateReceivableSetup = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: ReceivableSetup) => updateReceivableSetup(financialModelId, params),
    onSuccess: () => {
      queryClient.resetQueries({
        queryKey: RECEIVABLE_KEY
      })
      queryClient.resetQueries({
        queryKey: PERIOD_DATA_KEY
      })
      queryClient.resetQueries({
        queryKey: TOTALS_KEY
      })
    }
  })
}

type GETDefaultParams = {
  financialModelId: string
  period_type: PeriodTypeQuery
  view: PeriodTypeView
}

const getReceivablePeriodData = async (params: GETDefaultParams): Promise<ReceivablePeriodDataResponse> => {
  const response = await axiosInstance.get<ReceivablePeriodDataResponse>(`/financial-models/${params.financialModelId}/receivables/data-entry`, {
    params: {
      type: params.period_type,
      view: params.view
    }
  })
  return response.data
}

export const useGetReceivableQuery = (params: GETDefaultParams) =>
  useSuspenseQuery({
    queryKey: PERIOD_DATA_KEY.concat([params.financialModelId, params.period_type, params.view]),
    queryFn: () => getReceivablePeriodData(params)
  })

export const useGetReceivableDataQuery = ({ financialModelId, period_type, view }: GETDefaultParams) =>
  useQuery({
    queryKey: PERIOD_DATA_KEY.concat([financialModelId, period_type, view]),
    queryFn: () => getReceivablePeriodData({ financialModelId, period_type, view }),
    enabled: Boolean(financialModelId)
  })

export type UpdateReceivablePeriodDataParams = {
  financialModelId: string
  data: ReceivableDataPost
}

export const updateReceivablePeriodData = async (params: UpdateReceivablePeriodDataParams) => {
  const { financialModelId, data } = params
  const dataToSend = {
    ...data,
    year: Number(data.year),
    month: Number(data.month)
  }
  const response = await axiosInstance.post<ReceivablePeriodDataResponse>(`/financial-models/${financialModelId}/receivables/data-entry`, dataToSend)
  return response.data
}

export const useUpdateReceivablePeriodData = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: ReceivableDataPost) =>
      updateReceivablePeriodData({
        financialModelId,
        data
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PERIOD_DATA_KEY.concat([financialModelId])
      })
    }
  })
}

type SwitchReceivableColumnTypeParams = {
  financialModelId: string
  period_type: PeriodType
  input_type: ReceivableAllowedTypes
  column_name: ReceivableBaseFieldsKeys
  month?: number
  year?: number
  product_id?: string | null
  customer_segment_id?: string | null
}

const switchReceivableColumnType = async (params: SwitchReceivableColumnTypeParams) => {
  const { financialModelId, column_name, ...rest } = params
  const response = await axiosInstance.post<ReceivablePeriodDataResponse>(
    `/financial-models/${financialModelId}/receivables/config/${column_name}`,
    undefined,
    { params: rest }
  )
  return response.data
}

type SwitchReceivableQueryColumnTypeParams = Omit<SwitchReceivableColumnTypeParams, 'financialModelId'>

export const useSwitchReceivableColumnType = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: SwitchReceivableQueryColumnTypeParams) =>
      switchReceivableColumnType({
        ...params,
        financialModelId
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: PERIOD_DATA_KEY.concat([financialModelId])
      })
    }
  })
}

const getReceivableTotals = async (params: GETDefaultParams): Promise<ReceivableTotals> => {
  const response = await axiosInstance.get<ReceivableTotals>(`/financial-models/${params.financialModelId}/receivables/data-entry/totals`, {
    params: {
      type: params.period_type,
      view: params.view
    }
  })
  return response.data
}

export const useGetReceivableTotals = (params: GETDefaultParams) =>
  useSuspenseQuery({
    queryKey: TOTALS_KEY.concat([params.financialModelId, params.period_type, params.view]),
    queryFn: () => getReceivableTotals(params)
  })
